import type { ColumnDef, ExpandedState } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import axios from "axios";
import isEqual from "lodash/isEqual";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { useAuthContext } from "../../../../components/Auth";
import { LinkButtonLargeV2 } from "../../../../components/Buttons/Buttons";
import {
  CheckBoxNoLabel,
  IndeterminateCheckbox,
} from "../../../../components/CheckBoxes/CheckBoxes";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../components/Error";
import {
  CaretDownIcon,
  CaretLeft,
  InfoIcon,
} from "../../../../components/Icons/Icons";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { Table } from "../../../../components/Table/Table";
import {
  H3,
  SmallSectionHeaderRegular,
} from "../../../../components/Typography/Typography";
import { Form } from "../../../../layout/FormLayout";
import {
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageTitle,
} from "../../../../layout/portalPageLayout";
import { PageWrapper } from "../../../../layout/publicPageLayout";
import type { Permission } from "../../../../types/types";
import type { Role } from "../../../../types/types.PIM";
import { useRoutePath } from "../../../../util/Routing";
import { useFormWrapper, useStoreState } from "../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import { spacingTokens } from "../../../../designTokens";

const PRODUCT_MAPPING = [
  {
    parent: "view_products",
    children: [
      "view_product_type_master",
      "view_product_type_variant",
      "view_product_type_standard",
    ],
  },
  {
    parent: "modify_products",
    children: [
      "modify_product_type_master",
      "modify_product_type_variant",
      "modify_product_type_standard",
    ],
  },
  {
    parent: "delete_products",
    children: [
      "delete_product_type_master",
      "delete_product_type_variant",
      "delete_product_type_standard",
    ],
  },
  {
    parent: "publish_products",
    children: [
      "publish_product_type_master",
      "publish_product_type_variant",
      "publish_product_type_standard",
    ],
  },
  {
    parent: "unpublish_products",
    children: [
      "unpublish_product_type_master",
      "unpublish_product_type_variant",
      "unpublish_product_type_standard",
    ],
  },
] as const;

type FormOutput = { [K in Permission]: boolean };

type ViewModifyDelete = "view" | "modify" | "delete";

type TableRow = { [K in ViewModifyDelete]: JSX.Element } & { name: string };

type BaseTableRow = { [K in ViewModifyDelete]: JSX.Element } & { name: string };
type ProductTableRow = BaseTableRow & {
  id?: string;
  publish?: JSX.Element;
  unpublish?: JSX.Element;
  subRows?: ProductTableRow[];
};

const CaretWrapper = styled.div<{ isExpanded: boolean }>`
  transform: ${({ isExpanded }) =>
    isExpanded ? "rotate(0deg)" : "rotate(-90deg)"};
  transition: all 0.2s ease-out;
  display: flex;
  margin-right: ${({ theme }) => theme.primitiveTokens.spacing.spacing_sm};
  align-items: center;
`;

let previous: FormOutput | null = null;

function is_truthy(value: string[] | boolean | undefined) {
  if (Array.isArray(value)) {
    return value.length > 0;
  } else {
    return Boolean(value);
  }
}

const getToolTipText = (
  role: Role["name"] | undefined,
  category: "templates_products_assets" | "attributes_lists"
) => {
  if (role === "Admin") {
    return "Admin permissions cannot be modified";
  }
  if (category === "templates_products_assets") {
    return `Control permissions for ${role + "s"} on Templates & Products`;
  }
  if (category === "attributes_lists") {
    return `Control permissions for ${role + "s"} on Attributes & Lists`;
  }
};

export function SellerAdminRoleDetail() {
  const { t } = useTranslation();
  const { storefront_id, storefront_metadata } = useStoreState();
  const { getLabel } = useMdmEntityDisplayNames();
  const { role_id } = useParams<{ role_id: string }>();
  const { updatePermissions } = useAuthContext();
  const { adminPath } = useRoutePath();
  const { notifyError } = useContext(Notifications);
  const theme = useTheme();

  const [show_confirm_modal, set_show_confirm_modal] = useState(false);
  const [expandedRows, setExpandedRows] = useState<ExpandedState>({
    products: true,
  });
  const [attributesListsTableData, setAttributesListsTableData] = useState<
    TableRow[]
  >([]);
  const [
    templatesProductsAssetsTableData,
    setTemplatesProductsAssetsTableData,
  ] = useState<ProductTableRow[]>([]);

  const {
    data: role,
    error: roleError,
    revalidate,
  } = useSWR<Role, AxiosError>(
    `/v1/storefronts/${storefront_id}/roles/${role_id}/permissions`,
    {
      revalidateOnFocus: false,
    }
  );

  const isLoadingRole = !role && !roleError;

  const { control, register, watch, formState, setValue, getValues } =
    useFormWrapper<FormOutput>({
      shouldUnregister: false,
    });

  // The backend supports changing the permissions of the admin account from the
  // admin account. For local testing of permissions this is the line to change.
  const editDisabled = (role?.name as string) === "Admin";

  const onCancelPermissionUpdate = () => {
    set_show_confirm_modal(false);
    window.location.reload();
  };

  const onConfirmPermissionUpdate = async () => {
    if (data) {
      if (view_attributes_dialog_message) {
        data["view_attributes"] = true;
        if (is_truthy(data["modify_templates"])) {
          data["view_groups"] = true;
          data["view_collections"] = true;
        }
      }
      if (view_templates_dialog_message) {
        data["view_templates"] = true;
      }
      try {
        await axios.patch<Role>(
          `/v1/storefronts/${storefront_id}/roles/${role_id}/permissions`,
          {
            // bandaid for a very occasional bug where the form returns ["on"] or []
            // instead of true or false.
            permissions: Object.entries(data).map(([key, value]) => ({
              has_permission: Array.isArray(value)
                ? value.length > 0
                : typeof value === "string"
                ? value === "on"
                : Boolean(value),
              slug: key,
            })),
          }
        );
      } catch (error) {
        console.log(error);
        notifyError(t("There was an error saving the permissions"));
      } finally {
        set_show_confirm_modal(false);
        window.location.reload();
      }
    }
  };

  // columns and table data can be made fully dynamic once all permissions are supported.
  const tableColumns = React.useMemo<ColumnDef<TableRow>[]>(
    () => [
      {
        header: "",
        accessorKey: "name",
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("View")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "attributes_lists")}
              style={{ paddingTop: spacingTokens.spacing_xxs }}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
            <ReactTooltip id="info-icon-tip" />
          </div>
        ),
        accessorKey: "view",
        cell: (cell) => cell.renderValue(),
        enableSorting: false,
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("Create/Edit")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "attributes_lists")}
              style={{ paddingTop: spacingTokens.spacing_xxs }}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
            <ReactTooltip id="info-icon-tip" />
          </div>
        ),
        accessorKey: "modify",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("Delete/Archive")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "attributes_lists")}
              style={{ paddingTop: spacingTokens.spacing_xxs }}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
            <ReactTooltip id="info-icon-tip" />
          </div>
        ),
        accessorKey: "delete",
        cell: (cell) => cell.renderValue(),
      },
    ],
    [role, t, theme.secondaryIconColor]
  );

  const templatesProductsAndAssetsTableColumns = React.useMemo<
    ColumnDef<TableRow>[]
  >(
    () => [
      {
        header: "",
        accessorKey: "name",
        cell: ({ row, getValue }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 20}px`,
            }}
          >
            {row.getCanExpand() ? (
              <CaretWrapper
                isExpanded={row.getIsExpanded()}
                onClick={row.getToggleExpandedHandler()}
              >
                <CaretDownIcon height={15} width={15} />
              </CaretWrapper>
            ) : (
              <div
                style={{
                  marginRight: theme.primitiveTokens.spacing.spacing_sm,
                }}
              />
            )}
            {getValue<string>()}
          </div>
        ),
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("View")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "templates_products_assets")}
              style={{ paddingTop: spacingTokens.spacing_xxs }}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
            <ReactTooltip id="info-icon-tip" />
          </div>
        ),
        accessorKey: "view",
        cell: (cell) => cell.renderValue(),
        enableSorting: false,
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("Create/Edit")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "templates_products_assets")}
              style={{ paddingTop: spacingTokens.spacing_xxs }}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
            <ReactTooltip id="info-icon-tip" />
          </div>
        ),
        accessorKey: "modify",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("Delete/Archive")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "templates_products_assets")}
              style={{ paddingTop: spacingTokens.spacing_xxs }}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
            <ReactTooltip id="info-icon-tip" />
          </div>
        ),
        accessorKey: "delete",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("Publish")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "templates_products_assets")}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
          </div>
        ),
        accessorKey: "publish" as const,
        cell: (cell) => cell.renderValue(),
      },
      {
        header: () => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "3px" }}>{t("Unpublish")}</div>
            <div
              data-for="info-icon-tip"
              data-tip={getToolTipText(role?.name, "templates_products_assets")}
            >
              <InfoIcon
                fill={theme.secondaryIconColor}
                height={15}
                width={15}
              />
            </div>
          </div>
        ),
        accessorKey: "unpublish" as const,
        cell: (cell) => cell.renderValue(),
      },
    ],
    [
      theme.primitiveTokens.spacing.spacing_sm,
      theme.secondaryIconColor,
      t,
      role,
    ]
  );

  const handleExpandedState = (expanded: ExpandedState) => {
    setExpandedRows(expanded);
  };

  const hasPermissionInRole = useCallback(
    (permission: Permission) =>
      Boolean(
        role?.permissions.find(
          ({ is_active, permission: rolePermission }) =>
            rolePermission.slug === permission && is_active
        )
      ),
    [role]
  );

  const getPermissionValue = useCallback(
    (permission: Permission) => {
      const formValue = getValues()?.[permission];
      return formValue === undefined
        ? hasPermissionInRole(permission)
        : formValue;
    },
    [getValues, hasPermissionInRole]
  );

  const data: FormOutput | null = watch();

  const productPermissionSignature = useMemo(
    () =>
      PRODUCT_MAPPING.map(({ children }) =>
        children
          .map((child) =>
            is_truthy(data?.[child] ?? hasPermissionInRole(child))
          )
          .join(":")
      ).join("|"),
    [data, hasPermissionInRole]
  );

  const getParentCheckboxProps = useCallback(
    (children: readonly Permission[]) => {
      const checkedChildren = children.filter((child) =>
        is_truthy(getPermissionValue(child))
      ).length;

      return {
        checked: checkedChildren === children.length,
        indeterminate: checkedChildren > 0 && checkedChildren < children.length,
      };
    },
    [getPermissionValue]
  );

  const renderProductParentCheckbox = useCallback(
    (parent: Permission, children: readonly Permission[]) => {
      const { checked, indeterminate } = getParentCheckboxProps(children);

      return (
        <Controller
          control={control}
          name={parent}
          defaultValue={
            hasPermissionInRole(parent) ||
            children.some((child) => hasPermissionInRole(child))
          }
          render={({ onChange }) => (
            <IndeterminateCheckbox
              name={parent}
              checked={checked}
              indeterminate={indeterminate}
              disabled={editDisabled}
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                onChange(checked);
                children.forEach((child) =>
                  setValue(child, checked, { shouldDirty: true })
                );
              }}
            />
          )}
        />
      );
    },
    [
      control,
      editDisabled,
      getParentCheckboxProps,
      hasPermissionInRole,
      setValue,
    ]
  );

  useEffect(() => {
    const handleRolesData = (data: Role) => {
      const hasPermissionLocal = (p: Permission) => hasPermissionInRole(p);

      setAttributesListsTableData([
        {
          name: "Attributes",
          view: (
            <CheckBoxNoLabel
              ref={register}
              name="view_attributes"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("view_attributes")}
            />
          ),
          modify: (
            <CheckBoxNoLabel
              ref={register}
              name="modify_attributes"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("modify_attributes")}
            />
          ),
          delete: (
            <CheckBoxNoLabel
              ref={register}
              name="delete_attributes"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("delete_attributes")}
            />
          ),
        },
        {
          name: "Attribute Groups",
          view: (
            <CheckBoxNoLabel
              ref={register}
              name="view_groups"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("view_groups")}
            />
          ),
          modify: (
            <CheckBoxNoLabel
              ref={register}
              name="modify_groups"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("modify_groups")}
            />
          ),
          delete: (
            <CheckBoxNoLabel
              ref={register}
              name="delete_groups"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("delete_groups")}
            />
          ),
        },
        {
          name: "Attribute Collections",
          view: (
            <CheckBoxNoLabel
              ref={register}
              name="view_collections"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("view_collections")}
            />
          ),
          modify: (
            <CheckBoxNoLabel
              ref={register}
              name="modify_collections"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("modify_collections")}
            />
          ),
          delete: (
            <CheckBoxNoLabel
              ref={register}
              name="delete_collections"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("delete_collections")}
            />
          ),
        },
        {
          name: "Lists",
          view: (
            <CheckBoxNoLabel
              ref={register}
              name="view_lists"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("view_lists")}
            />
          ),
          modify: (
            <CheckBoxNoLabel
              ref={register}
              name="modify_lists"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("modify_lists")}
            />
          ),
          delete: (
            <CheckBoxNoLabel
              ref={register}
              name="delete_lists"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("delete_lists")}
            />
          ),
        },
      ]);

      setTemplatesProductsAssetsTableData([
        {
          name: "Templates",
          view: (
            <CheckBoxNoLabel
              ref={register}
              name="view_templates"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("view_templates")}
            />
          ),
          modify: (
            <CheckBoxNoLabel
              ref={register}
              name="modify_templates"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("modify_templates")}
            />
          ),
          delete: (
            <CheckBoxNoLabel
              ref={register}
              name="delete_templates"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("delete_templates")}
            />
          ),
        },
        {
          name: "Assets",
          view: (
            <CheckBoxNoLabel
              ref={register}
              name="view_assets"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("view_assets")}
            />
          ),
          modify: (
            <CheckBoxNoLabel
              ref={register}
              name="modify_assets"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("modify_assets")}
            />
          ),
          delete: (
            <CheckBoxNoLabel
              ref={register}
              name="delete_assets"
              disabled={editDisabled}
              defaultChecked={hasPermissionLocal("delete_assets")}
            />
          ),
        },
        {
          id: "products",
          name: "Products",
          view: renderProductParentCheckbox(
            "view_products",
            PRODUCT_MAPPING[0].children
          ),
          modify: renderProductParentCheckbox(
            "modify_products",
            PRODUCT_MAPPING[1].children
          ),
          delete: renderProductParentCheckbox(
            "delete_products",
            PRODUCT_MAPPING[2].children
          ),
          publish: renderProductParentCheckbox(
            "publish_products",
            PRODUCT_MAPPING[3].children
          ),
          unpublish: renderProductParentCheckbox(
            "unpublish_products",
            PRODUCT_MAPPING[4].children
          ),
          subRows: role?.enable_product_type_visibility
            ? [
                {
                  id: "product-master",
                  name: "Master",
                  view: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="view_product_type_master"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "view_product_type_master"
                      )}
                    />
                  ),
                  modify: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="modify_product_type_master"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "modify_product_type_master"
                      )}
                    />
                  ),
                  delete: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="delete_product_type_master"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "delete_product_type_master"
                      )}
                    />
                  ),
                  publish: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="publish_product_type_master"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "publish_product_type_master"
                      )}
                    />
                  ),
                  unpublish: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="unpublish_product_type_master"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "unpublish_product_type_master"
                      )}
                    />
                  ),
                },
                {
                  id: "product-variant",
                  name: "Variant",
                  view: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="view_product_type_variant"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "view_product_type_variant"
                      )}
                    />
                  ),
                  modify: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="modify_product_type_variant"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "modify_product_type_variant"
                      )}
                    />
                  ),
                  delete: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="delete_product_type_variant"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "delete_product_type_variant"
                      )}
                    />
                  ),
                  publish: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="publish_product_type_variant"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "publish_product_type_variant"
                      )}
                    />
                  ),
                  unpublish: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="unpublish_product_type_variant"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "unpublish_product_type_variant"
                      )}
                    />
                  ),
                },
                {
                  id: "product-standard",
                  name: "Standalone",
                  view: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="view_product_type_standard"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "view_product_type_standard"
                      )}
                    />
                  ),
                  modify: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="modify_product_type_standard"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "modify_product_type_standard"
                      )}
                    />
                  ),
                  delete: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="delete_product_type_standard"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "delete_product_type_standard"
                      )}
                    />
                  ),
                  publish: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="publish_product_type_standard"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "publish_product_type_standard"
                      )}
                    />
                  ),
                  unpublish: (
                    <CheckBoxNoLabel
                      ref={register}
                      name="unpublish_product_type_standard"
                      disabled={editDisabled}
                      defaultChecked={hasPermissionLocal(
                        "unpublish_product_type_standard"
                      )}
                    />
                  ),
                },
              ]
            : [],
        },
        ...(storefront_metadata?.enable_supplier
          ? [
              {
                name: getLabel("supplier", "plural"),
                view: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="view_suppliers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("view_suppliers")}
                  />
                ),
                modify: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="modify_suppliers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("modify_suppliers")}
                  />
                ),
                delete: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="delete_suppliers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("delete_suppliers")}
                  />
                ),
                publish: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="publish_suppliers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("publish_suppliers")}
                  />
                ),
                unpublish: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="unpublish_suppliers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("unpublish_suppliers")}
                  />
                ),
              },
            ]
          : []),
        ...(storefront_metadata?.enable_customer
          ? [
              {
                name: getLabel("customer", "plural"),
                view: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="view_customers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("view_customers")}
                  />
                ),
                modify: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="modify_customers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("modify_customers")}
                  />
                ),
                delete: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="delete_customers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("delete_customers")}
                  />
                ),
                publish: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="publish_customers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("publish_customers")}
                  />
                ),
                unpublish: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="unpublish_customers"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("unpublish_customers")}
                  />
                ),
              },
            ]
          : []),
        ...(storefront_metadata?.enable_distributor
          ? [
              {
                name: getLabel("distributor", "plural"),
                view: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="view_distributors"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("view_distributors")}
                  />
                ),
                modify: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="modify_distributors"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("modify_distributors")}
                  />
                ),
                delete: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="delete_distributors"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("delete_distributors")}
                  />
                ),
                publish: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="publish_distributors"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal("publish_distributors")}
                  />
                ),
                unpublish: (
                  <CheckBoxNoLabel
                    ref={register}
                    name="unpublish_distributors"
                    disabled={editDisabled}
                    defaultChecked={hasPermissionLocal(
                      "unpublish_distributors"
                    )}
                  />
                ),
              },
            ]
          : []),
      ]);
    };

    if (role) {
      handleRolesData(role);
    }
  }, [
    register,
    role,
    editDisabled,
    hasPermissionInRole,
    productPermissionSignature,
    renderProductParentCheckbox,
    storefront_metadata?.enable_supplier,
    storefront_metadata?.enable_customer,
    storefront_metadata?.enable_distributor,
    getLabel,
  ]);

  const view_attributes_dialog_message = useMemo(() => {
    return !is_truthy(data["view_attributes"]) &&
      is_truthy(data["modify_groups"])
      ? t(
          "To be able to modify groups, you must have permission to view attributes."
        )
      : !is_truthy(data["view_attributes"]) &&
        is_truthy(data["modify_collections"])
      ? t(
          "To be able to modify collections, you must have permission to view attributes."
        )
      : (!is_truthy(data["view_attributes"]) ||
          !is_truthy(data["view_groups"]) ||
          !is_truthy(data["view_collections"])) &&
        is_truthy(data["modify_templates"])
      ? t(
          "To be able to modify templates, you must have permission to view attributes, groups, and collections."
        )
      : null;
  }, [data, t]);

  const view_templates_dialog_message = useMemo(() => {
    return !is_truthy(data["view_templates"]) &&
      is_truthy(data["modify_products"])
      ? t(
          "To be able to modify products, you must have permission to view templates."
        )
      : null;
  }, [data, t]);

  const prevDataRef = useRef<Partial<FormOutput>>({});

  useEffect(() => {
    const prev = prevDataRef.current;
    PRODUCT_MAPPING.forEach(({ parent, children }) => {
      const prevParentChecked = is_truthy(prev[parent] ?? false);
      const prevAnyChildChecked = children.some((child) =>
        is_truthy(prev[child] ?? false)
      );
      const parentChecked = is_truthy(data[parent]);
      const anyChildChecked = children.some((child) => is_truthy(data[child]));

      // Parent just got unchecked - uncheck all children
      if (prevParentChecked && !parentChecked) {
        children.forEach((child) => setValue(child, false));
      }

      // A child just got checked but parent is unchecked - check parent
      if (anyChildChecked && !prevAnyChildChecked && !parentChecked) {
        setValue(parent, true);
      }

      if (prevAnyChildChecked && !anyChildChecked && parentChecked) {
        setValue(parent, false);
      }
    });

    prevDataRef.current = data;
  }, [data, setValue]);

  useEffect(() => {
    const autoSaveForm = async (data: FormOutput) => {
      if (view_attributes_dialog_message || view_templates_dialog_message) {
        set_show_confirm_modal(true);
      } else {
        try {
          await axios.patch<Role>(
            `/v1/storefronts/${storefront_id}/roles/${role_id}/permissions`,
            {
              // bandaid for a very occasional bug where the form returns ["on"] or []
              // instead of true or false.
              permissions: Object.entries(data).map(([key, value]) => ({
                has_permission: Array.isArray(value)
                  ? value.length > 0
                  : typeof value === "string"
                  ? value === "on"
                  : Boolean(value),
                slug: key,
              })),
            }
          );
        } catch (error) {
          notifyError(t("There was an error saving the permissions"));
        }
      }
    };
    if (
      !isLoadingRole &&
      formState.isDirty &&
      data !== null &&
      Object.keys(data).length > 0 &&
      !isEqual(data, previous)
    ) {
      previous = data;

      autoSaveForm(data);
    }
  }, [
    isLoadingRole,
    data,
    formState,
    storefront_id,
    role_id,
    updatePermissions,
    revalidate,
    notifyError,
    t,
    view_attributes_dialog_message,
    view_templates_dialog_message,
  ]);

  if (isLoadingRole) {
    return <DelayedSpinner />;
  }

  if (roleError) {
    return (
      <ErrorPlaceholder message={t("There was an error loading the role")} />
    );
  }

  if (role) {
    return (
      <PageWrapper>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Link to={`${adminPath}/organization/roles`}>
            <LinkButtonLargeV2>
              <CaretLeft />
              {t("Back")}
            </LinkButtonLargeV2>
          </Link>
          <PageTitle>{role.name}</PageTitle>
        </div>
        <FullWidthHorizontalSeparator />
        <ContentWrapper style={{ marginTop: spacingTokens.spacing_3xl }}>
          <Form>
            <H3>{t("Attributes & Lists")}</H3>
            <SmallSectionHeaderRegular>
              <span>
                {`Control permissions for ${role.name + "s"} on
                Attributes & Lists.`}
              </span>
            </SmallSectionHeaderRegular>
            <Table
              columns={tableColumns}
              data={attributesListsTableData}
              error={undefined}
              isLoading={false}
              lastChildleftAlign
            />
          </Form>
          <Form>
            <H3>{t("Templates, Products, Assets, and Companies")}</H3>
            <SmallSectionHeaderRegular>
              <span>
                {`Control permissions for ${
                  role.name + "s"
                } on Templates, Products, Assets, and Companies`}
              </span>
            </SmallSectionHeaderRegular>
            <Table
              columns={templatesProductsAndAssetsTableColumns}
              data={templatesProductsAssetsTableData}
              subRowsKey="subRows"
              expandedRows={expandedRows}
              handleExpandedState={handleExpandedState}
              isLoading={false}
              error={undefined}
              lastChildleftAlign
            />
          </Form>
        </ContentWrapper>
        {show_confirm_modal && (
          <ConfirmDialog
            show={show_confirm_modal}
            closeDialog={onCancelPermissionUpdate}
            confirmMessage={
              view_attributes_dialog_message ||
              view_templates_dialog_message ||
              ""
            }
            handleCancel={onCancelPermissionUpdate}
            handleConfirm={onConfirmPermissionUpdate}
          />
        )}
      </PageWrapper>
    );
  } else return null;
}
